import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Smart scroll manager for SPA navigation.
 *
 * • PUSH/REPLACE (clicked a link, new route): scroll to top.
 * • POP (back/forward button): restore the scroll position that was
 *   stored for that pathname when the user left.
 *
 * Race-condition guard for async-rendered pages:
 *   The naive `scrollTo(0, savedY)` clamps to whatever the page height is
 *   at that moment. If the page is still loading rails, it lands at the
 *   half-rendered bottom (looks like scroll-to-bottom on mobile).
 *
 * Fix:
 *   - While waiting for content to grow, STAY AT TOP (don't pre-scroll to
 *     maxReachable — that was the bug causing "back lands at bottom" on
 *     mobile when rails hadn't finished loading).
 *   - Snap to savedY only when scrollHeight is finally tall enough.
 *   - Wait 250ms before activating user-interrupt detection (mobile fires
 *     touchmove during the layout settle, which used to cancel the
 *     restore loop immediately).
 *   - Use scroll deltas, not absolute Y, to detect real user interruption.
 */
const KEY = "fm.scroll.";
// v2.46 — Extended from 3s to 8s. The Home / Collection pages lazy-mount
// many rails (LazyMount + IntersectionObserver triggers) and on production
// network conditions the doc-bundle data can take 4-6s to settle. With the
// old 3s ceiling the restore loop bailed too early on slow connections and
// the user landed back at the top — exactly the regression they reported.
const RESTORE_TIMEOUT_MS = 8000;
const RESTORE_POLL_MS = 80;
const USER_INTERRUPT_DELAY_MS = 250;

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const restoreCancelRef = useRef(null);

  // Disable native scroll restoration — we own the behavior end-to-end.
  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save scroll position throttled on every scroll while user is on this route.
  useLayoutEffect(() => {
    let saveTimer = null;
    const save = () => {
      try { sessionStorage.setItem(KEY + pathname, String(window.scrollY)); } catch (_) {}
    };
    const throttledSave = () => {
      if (saveTimer) return;
      saveTimer = setTimeout(() => { save(); saveTimer = null; }, 100);
    };
    window.addEventListener("scroll", throttledSave, { passive: true });
    // Final flush on pagehide / visibility change — survives tab close.
    window.addEventListener("pagehide", save);
    return () => {
      if (saveTimer) clearTimeout(saveTimer);
      save();
      window.removeEventListener("scroll", throttledSave);
      window.removeEventListener("pagehide", save);
    };
  }, [pathname]);

  // Decide where to scroll when the location changes.
  useLayoutEffect(() => {
    if (restoreCancelRef.current) {
      restoreCancelRef.current();
      restoreCancelRef.current = null;
    }
    if (hash) return;

    if (navType !== "POP") {
      // Fresh navigation — always start at the top.
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }

    // POP (back/forward) — restore saved Y, but only once content is tall enough.
    let savedY = 0;
    try { savedY = parseInt(sessionStorage.getItem(KEY + pathname) || "0", 10) || 0; } catch (_) {}
    if (savedY <= 0) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }

    // Pin to top immediately. This is the KEY FIX vs the previous impl —
    // we never scroll to the half-rendered bottom while waiting for content.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    const startedAt = performance.now();
    let cancelled = false;
    let userInterrupted = false;
    let interruptArmed = false;
    let lastProgrammaticY = 0;

    // Arm user-interrupt detection only AFTER the initial layout settles.
    // Mobile fires touchmove during the route swap; without this delay we'd
    // cancel ourselves immediately and the user lands at the top forever.
    const armTimer = setTimeout(() => { interruptArmed = true; }, USER_INTERRUPT_DELAY_MS);

    const onUserScroll = () => {
      if (!interruptArmed || cancelled) return;
      // Only count manual scrolls that deviate >24px from the last value WE set.
      if (Math.abs(window.scrollY - lastProgrammaticY) > 24) {
        userInterrupted = true;
      }
    };
    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchmove", onUserScroll, { passive: true });

    const attempt = () => {
      if (cancelled || userInterrupted) return;
      const docHeight = document.documentElement.scrollHeight;
      const viewport = window.innerHeight;
      const maxReachable = Math.max(0, docHeight - viewport);
      if (maxReachable >= savedY) {
        lastProgrammaticY = savedY;
        window.scrollTo({ top: savedY, left: 0, behavior: "instant" });
        return; // success
      }
      // Content not tall enough yet — stay at top, just retry.
      if (performance.now() - startedAt < RESTORE_TIMEOUT_MS) {
        setTimeout(attempt, RESTORE_POLL_MS);
      } else {
        // Timed out — page never grew tall enough. Stay at top.
      }
    };
    // First attempt on next frame so React has a chance to render the rails.
    requestAnimationFrame(attempt);

    restoreCancelRef.current = () => {
      cancelled = true;
      clearTimeout(armTimer);
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchmove", onUserScroll);
    };
  }, [pathname, hash, navType]);

  return null;
}
